import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {lastValueFrom} from "rxjs";
import {DBSchema, IDBPDatabase, openDB} from "idb";
import {AlbaranService} from "./albaran.service";
import {Deca} from "../models/deca";
import {todayLocal} from "./date-helper";

/**
 * Copia local de los DeCAs del día del xofer, para poder enseñar el QR y abrir el PDF aunque el
 * móvil se quede sin cobertura. Se descarga al arrancar la app y cada vez que hay conexión en un
 * día nuevo, y solo se sustituye cuando la descarga sale bien: siempre queda la copia más reciente
 * que se pudo bajar, con la hora en que se bajó para saber si es actual.
 *
 * La lista (URLs) va en localStorage; los PDF, en IndexedDB: localStorage solo admite texto y unos
 * 5 MB en total, y un DeCA puede pesar hasta 5 MB cada uno.
 */
export interface DecaBackup {
    date: string;          // fecha de transporte (Y-m-d) de los DeCAs guardados
    downloadedAt: string;  // ISO de cuándo se descargó la copia
    items: Deca[];
}

export interface DecaPdfCopy {
    idAlbara: number;
    url: string;           // URL pública de la versión guardada: si cambia, hay que volver a bajarlo
    downloadedAt: string;
    pdf: Blob;
}

export interface DecaDayPdfCopy {
    key: string;           // siempre 'day': solo se guarda el último PDF del día descargado
    date: string;
    downloadedAt: string;
    pdf: Blob;
}

interface DecaDb extends DBSchema {
    pdfs: { key: number; value: DecaPdfCopy };
    dayPdf: { key: string; value: DecaDayPdfCopy };
}

@Injectable({
    providedIn: 'root'
})
export class DecaCacheService {

    private static readonly STORAGE_KEY = 'decasBackup';
    private static readonly DAY_KEY = 'day';

    private dbPromise: Promise<IDBPDatabase<DecaDb>>;
    private refreshing: Promise<DecaBackup | null> | null = null;

    constructor(
        private albaranService: AlbaranService,
        private http: HttpClient,
    ) {
        this.dbPromise = openDB<DecaDb>('decasDB', 2, {
            // Crear solo lo que falte: un navegador puede traer una versión anterior de la base
            upgrade(db) {
                if (!db.objectStoreNames.contains('pdfs')) {
                    db.createObjectStore('pdfs', {keyPath: 'idAlbara'});
                }
                if (!db.objectStoreNames.contains('dayPdf')) {
                    db.createObjectStore('dayPdf', {keyPath: 'key'});
                }
            }
        });
    }

    /**
     * Descarga los DeCAs de hoy (rutas finalizadas incluidas, para que la copia sea completa) y,
     * si la lista llega bien, sus PDF y el PDF de todo el día. Nada se sustituye si su descarga
     * falla. Varias llamadas seguidas comparten la misma descarga.
     */
    refresh(): Promise<DecaBackup | null> {
        if (!this.refreshing) {
            this.refreshing = this.doRefresh().finally(() => this.refreshing = null);
        }

        return this.refreshing;
    }

    private async doRefresh(): Promise<DecaBackup | null> {
        const today = todayLocal();

        try {
            const response: any = await lastValueFrom(this.albaranService.getDecas(today, true));
            const backup: DecaBackup = {
                date: today,
                downloadedAt: new Date().toISOString(),
                items: response.items ?? [],
            };

            localStorage.setItem(DecaCacheService.STORAGE_KEY, JSON.stringify(backup));

            await Promise.allSettled([
                this.refreshPdfs(backup.items),
                this.refreshDayPdf(today, backup.items),
            ]);

            return backup;
        } catch (error) {
            return this.get();
        }
    }

    // El PDF de cada DeCA con URL. Uno ya guardado con la misma URL (misma versión) no se vuelve a
    // bajar; los que ya no están en la lista de hoy se quitan.
    private async refreshPdfs(items: Deca[]): Promise<void> {
        const db = await this.dbPromise;
        const wanted = items.filter(deca => !!deca.url);
        const wantedIds = new Set(wanted.map(deca => deca.idAlbara));

        for (const key of await db.getAllKeys('pdfs')) {
            if (!wantedIds.has(key)) {
                await db.delete('pdfs', key);
            }
        }

        await Promise.allSettled(wanted.map(async deca => {
            const stored = await db.get('pdfs', deca.idAlbara);
            if (stored && stored.url === deca.url) return;

            const pdf = await lastValueFrom(this.http.get(deca.url!, {responseType: 'blob'}));
            await db.put('pdfs', {
                idAlbara: deca.idAlbara,
                url: deca.url!,
                downloadedAt: new Date().toISOString(),
                pdf: pdf,
            });
        }));
    }

    // El PDF con todos los DeCAs del día (rutas finalizadas incluidas). Sin ningún DeCA con URL no
    // hay fichero que bajar: se quita la copia anterior para no ofrecer un día que ya no es.
    private async refreshDayPdf(date: string, items: Deca[]): Promise<void> {
        const db = await this.dbPromise;

        if (!items.some(deca => !!deca.url)) {
            await db.delete('dayPdf', DecaCacheService.DAY_KEY);
            return;
        }

        const pdf = await lastValueFrom(this.albaranService.getDecasPdf(date, true));
        await db.put('dayPdf', {
            key: DecaCacheService.DAY_KEY,
            date: date,
            downloadedAt: new Date().toISOString(),
            pdf: pdf,
        });
    }

    get(): DecaBackup | null {
        const stored = localStorage.getItem(DecaCacheService.STORAGE_KEY);

        if (!stored) return null;

        try {
            return JSON.parse(stored);
        } catch (error) {
            return null;
        }
    }

    // Si la copia guardada es del día de hoy
    isFromToday(): boolean {
        return this.get()?.date === todayLocal();
    }

    async getPdf(idAlbara: number): Promise<DecaPdfCopy | undefined> {
        const db = await this.dbPromise;
        return db.get('pdfs', idAlbara);
    }

    async getDayPdf(): Promise<DecaDayPdfCopy | undefined> {
        const db = await this.dbPromise;
        return db.get('dayPdf', DecaCacheService.DAY_KEY);
    }

    /**
     * Abre el PDF de un DeCA: con conexión, la URL pública en una pestaña nueva; sin conexión (o si
     * quien llama ya sabe que el servidor no responde), la copia guardada como descarga.
     *
     * Antes de abrir la URL se comprueba que el servidor responda: navigator.onLine no se entera
     * de que el móvil ha perdido la cobertura, y una pestaña con la URL fallaría en silencio. La
     * pestaña se abre en blanco ANTES de la comprobación para no perder el gesto del usuario (los
     * navegadores móviles bloquean window.open después de un await).
     */
    async openPdf(idAlbara: number, url: string | null, fileName: string, offline: boolean = false): Promise<'online' | 'backup' | 'none'> {
        if (url && !offline && navigator.onLine !== false) {
            const tab = window.open('', '_blank');

            if (await this.isServerReachable(idAlbara)) {
                if (tab) {
                    tab.location.href = url;
                } else {
                    window.open(url, '_blank');
                }

                return 'online';
            }

            tab?.close();
        }

        const copy = await this.getPdf(idAlbara);
        if (!copy) return 'none';

        DecaCacheService.download(copy.pdf, fileName);

        return 'backup';
    }

    // Sonda ligera contra la API (no contra la URL pública, cuyo acceso queda auditado como lectura
    // de QR). Cualquier respuesta HTTP cuenta como servidor alcanzable; solo el silencio es "sin red".
    private async isServerReachable(idAlbara: number): Promise<boolean> {
        try {
            await lastValueFrom(this.albaranService.getDeca(idAlbara));
            return true;
        } catch (error: any) {
            const status = error?.status ?? 0;
            return status !== 0 && status !== 502 && status !== 503 && status !== 504;
        }
    }

    /** Descarga la última copia guardada del PDF del día. Devuelve la copia usada, o null si no hay. */
    async downloadDayPdfCopy(): Promise<DecaDayPdfCopy | null> {
        const copy = await this.getDayPdf();
        if (!copy) return null;

        DecaCacheService.download(copy.pdf, 'DECA_' + copy.date + '.pdf');

        return copy;
    }

    static download(pdf: Blob, fileName: string): void {
        const fileURL = URL.createObjectURL(pdf);
        const a = document.createElement('a');
        a.href = fileURL;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(fileURL);
    }

    async clear(): Promise<void> {
        localStorage.removeItem(DecaCacheService.STORAGE_KEY);
        const db = await this.dbPromise;
        await db.clear('pdfs');
        await db.clear('dayPdf');
    }
}
